/**
 * Display Image Caching System using IndexedDB
 * 
 * Flow:
 * 1. Check if image exists in IndexedDB with same URL
 * 2. If not exist → fetch from API and cache
 * 3. Display from IndexedDB
 */

import { openDB } from 'idb';

const DB_NAME = 'display-image-cache-db';
const STORE_NAME = 'display-image-cache';

interface CachedDisplayImageData {
  blob: Blob;
  url: string;
  cachedAt: number;
}

async function getDB() {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      db.createObjectStore(STORE_NAME);
    },
  });
}

export async function getCachedDisplayImage(url: string): Promise<CachedDisplayImageData | undefined> {
  try {
    const db = await getDB();
    return await db.get(STORE_NAME, url);
  } catch (error) {
    console.error('Error getting cached display image:', error);
    return undefined;
  }
}

export async function setCachedDisplayImage(url: string, blob: Blob): Promise<void> {
  try {
    const db = await getDB();
    await db.put(STORE_NAME, { blob, url, cachedAt: Date.now() }, url);
  } catch (error) {
    console.error('Error setting cached display image:', error);
  }
}

export async function deleteCachedDisplayImage(url: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete(STORE_NAME, url);
  } catch (error) {
    console.error('Error deleting cached display image:', error);
  }
}

export async function getAllCachedDisplayImages(): Promise<CachedDisplayImageData[]> {
  try {
    const db = await getDB();
    return await db.getAll(STORE_NAME);
  } catch (error) {
    console.error('Error getting all cached display images:', error);
    return [];
  }
}

export async function clearDisplayImageCache(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear(STORE_NAME);
    console.log('Display image cache cleared successfully');
  } catch (error) {
    console.error('Error clearing display image cache:', error);
  }
}

export async function getDisplayImageCacheStats(): Promise<{ count: number; totalSize: number }> {
  try {
    const images = await getAllCachedDisplayImages();
    const totalSize = images.reduce((sum, img) => sum + img.blob.size, 0);
    return { count: images.length, totalSize };
  } catch (error) {
    console.error('Error getting display image cache stats:', error);
    return { count: 0, totalSize: 0 };
  }
} 